import { setStorageOwner, clearCache } from './storageUrls.js';
/**
 * Firebase Auth client (browser).
 *
 * vision_web authenticates against the rrecce gRPC backend with a Firebase ID token. This module
 * owns the Firebase Auth session, which the rrecce web app on the same origin signs in and out
 * of; app code reads a fresh token per request via getIdTokenForRequest(). The
 * `vision_firebase_id_token` localStorage mirror is maintained solely as an e2e test seam (the
 * suite reads it to build request/subprotocol auth) — no app code reads it.
 */

import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, onIdTokenChanged, signOut } from 'firebase/auth';
import { config } from '../config';

const ID_TOKEN_STORAGE_KEY = 'vision_firebase_id_token';

const firebaseConfig = {
  apiKey: config.FIREBASE_API_KEY,
  authDomain: config.FIREBASE_AUTH_DOMAIN,
  projectId: config.FIREBASE_PROJECT_ID,
  storageBucket: config.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: config.FIREBASE_MESSAGING_SENDER_ID,
  appId: config.FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
let signingOut = false;

function storeIdToken(token) {
  try {
    if (token) localStorage.setItem(ID_TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
  } catch { /* private browsing / quota — token simply won't persist */ }
}

onIdTokenChanged(auth, async (user) => {
  if (!user) {
    setStorageOwner(null);
    storeIdToken(null);
    return;
  }

  try {
    const token = await user.getIdToken();
    if (auth.currentUser !== user || signingOut) return;
    setStorageOwner(user.emailVerified ? user.uid : null);
    storeIdToken(token);
  } catch {
    storeIdToken(null);
  }
});

export function firebaseAuthReady() {
  return auth.authStateReady();
}

export async function getIdTokenForRequest() {
  await auth.authStateReady();

  const user = auth.currentUser;
  if (!user) return null;

  try {
    const token = await user.getIdToken();
    if (auth.currentUser !== user || signingOut) return null;
    setStorageOwner(user.emailVerified ? user.uid : null);
    return token;
  } catch {
    return null;
  }
}

export function currentFirebaseUser() {
  return auth.currentUser;
}

// Firebase shares its sign-in state across the tabs of this origin, so this also hears a
// sign-out or sign-in made in another tab
export function onFirebaseUserChanged(callback) {
  return onAuthStateChanged(auth, callback);
}

export async function getFirebaseClaims() {
  if (!auth.currentUser) return {};

  const result = await auth.currentUser.getIdTokenResult();

  return result.claims || {};
}

export async function refreshFirebaseIdToken() {
  if (!auth.currentUser) return null;

  const token = await auth.currentUser.getIdToken(true);
  storeIdToken(token);

  return token;
}

export async function firebaseSignOut() {
  signingOut = true;
  clearCache();
  try { await signOut(auth); } finally { signingOut = false; }
  storeIdToken(null);
}
